import * as Sentry from '@sentry/react';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import type { NavigationScreenIntegrationState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbItem,
    Breadcrumbs,
    Button,
    confirm,
    Description,
    Dialog,
    DialogRoot,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Group,
    Heading,
    Label,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemIndicator,
    type ListRenderItemInfo,
    LoadingPanel,
    Page,
    PageHeader,
    PageMain,
    PageTabs,
    PageTabsIntroSection,
    Pagination,
    Panel,
    PanelBody,
    PanelSet,
    Tab,
    Tabs,
    toast,
    useDialogTriggerState,
    useScreenType,
} from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';
import { assertIsDefined, assertNever, type MandateProps } from '@gitbook/util-ts';

import {
    assertIsExtendedSiteInstallation,
    assertIsExtendedSpaceInstallation,
    getSiteFromOrgAllContent,
    integrationNeedsConfiguration,
    SpaceIcon,
} from '../../Components';
import {
    NavigationLink,
    NavigationRedirect,
    NavigationScreenWithOrganization,
    useNavigation,
    useSetAnalyticsScreen,
} from '../../navigation';
import {
    apiQuery,
    apiResource,
    getSitePermissions,
    useSuspensePaginatedAPIQuery,
} from '../../state';
import { IntegrationIntro, IntegrationOverview, IntegrationsEmptyState } from './components';
import { IntegrationInstallationDialogContent } from './components/IntegrationInstallationDialogContent';
import { useIntegrationScreen } from './hooks';
import {
    uninstallIntegrationFromOrganization,
    uninstallIntegrationFromSite,
    uninstallIntegrationFromSpace,
} from './integrations.api';
import { getContentDataFromInstallationContext } from './integrations.utils';
import type { ContentInstallationContext } from './types';

type IntegrationInstallationContext = {
    organization: api.Organization;
    integration: api.Integration;
    installation: api.IntegrationInstallation | null;
};

/**
 * Screen for an integration.
 */
export function IntegrationScreen(props: {
    screen: NavigationScreenIntegrationState;
}): React.ReactElement {
    return (
        <Sentry.ErrorBoundary fallback={<IntegrationsEmptyState kind="error" />}>
            <NavigationScreenWithOrganization screen={props.screen}>
                {() => <IntegrationsScreenWithOrganization />}
            </NavigationScreenWithOrganization>
        </Sentry.ErrorBoundary>
    );
}

function IntegrationsScreenWithOrganization(): React.ReactElement {
    const screen = useIntegrationScreen();
    const [{ data: organization }, { data: integration }, { data: installation }] =
        useSuspenseQueries({
            queries: [
                apiQuery('getOrganizationById', { organizationId: screen.organization }),
                apiQuery(
                    'getIntegrationByName',
                    { integrationName: screen.integration },
                    { nullOnError: true }
                ),
                apiQuery(
                    'getIntegrationInstallationById',
                    {
                        integrationName: screen.integration,
                        installationId: getIntegrationInstallationKey(screen.integration, {
                            organization: screen.organization,
                        }),
                    },
                    { nullOnError: true }
                ),
            ],
        });

    if (!integration) {
        return (
            <NavigationRedirect
                to={{
                    screen: {
                        type: 'integrations',
                    },
                }}
                replace
            />
        );
    }

    return (
        <React.Suspense fallback={<LoadingPanel />}>
            <IntegrationScreenWithData
                organization={organization}
                integration={integration}
                installation={installation}
            />
        </React.Suspense>
    );
}

function IntegrationScreenWithData(props: IntegrationInstallationContext): React.ReactElement {
    const screen = useIntegrationScreen();
    const { organization, integration, installation } = props;

    useHeadTitle(`${integration.title} - Integrations`);

    useSetAnalyticsScreen(
        {
            screen: 'integration',
            integration: integration.name,
            organization: screen.organization,
            screenTab: screen.tab,
        },
        [integration.name, screen.organization, screen.tab]
    );

    return (
        <Page>
            <IntegrationBreadcrumbs integration={integration} />

            <PageTabsIntroSection className="pb-10">
                <IntegrationIntro integration={integration} screen={screen} />
            </PageTabsIntroSection>

            <IntegrationTabs
                screen={screen}
                integration={integration}
                organization={organization}
                isInstalledOnOrg={Boolean(installation)}
            />

            <PageMain>
                <React.Suspense fallback={<LoadingPanel />}>
                    {(() => {
                        switch (screen.tab) {
                            case 'about':
                            case undefined:
                                return <IntegrationOverview integration={integration} />;

                            case 'installations':
                                return installation ? (
                                    <Sentry.ErrorBoundary
                                        fallback={
                                            <IntegrationInstallationsEmptyState
                                                integration={integration}
                                            />
                                        }
                                    >
                                        <PanelSet className="flex flex-col gap-10">
                                            {integration.target === api.IntegrationTarget.Site ||
                                            integration.target === api.IntegrationTarget.All ? (
                                                <IntegrationSiteInstallations
                                                    {...props}
                                                    installation={installation}
                                                />
                                            ) : null}
                                            {integration.target === api.IntegrationTarget.Space ||
                                            integration.target === api.IntegrationTarget.All ? (
                                                <IntegrationSpaceInstallations
                                                    {...props}
                                                    installation={installation}
                                                />
                                            ) : null}

                                            {organization.permissions.installIntegration ? (
                                                <DangerZonePanel
                                                    {...props}
                                                    installation={installation}
                                                />
                                            ) : null}
                                        </PanelSet>
                                    </Sentry.ErrorBoundary>
                                ) : (
                                    <NavigationRedirect
                                        to={{ screen: { type: 'integration', tab: 'about' } }}
                                    />
                                );

                            default:
                                assertNever(screen.tab);
                        }
                    })()}
                </React.Suspense>
            </PageMain>

            {screen.installation ? (
                <IntegrationInstallationDialog integration={integration} screen={screen} />
            ) : null}
        </Page>
    );
}

/**
 * Breadcrumbs for the integration screen or modal.
 */
function IntegrationBreadcrumbs(props: { integration: api.Integration }): React.ReactElement {
    const { integration } = props;
    const { isMobileOrTablet } = useScreenType();

    return (
        <PageHeader>
            <Breadcrumbs>
                <BreadcrumbItem
                    href={{
                        screen: {
                            type: 'integrations',
                        },
                    }}
                    iconOnly={isMobileOrTablet}
                >
                    Integrations
                </BreadcrumbItem>
                <BreadcrumbItem
                    href={{
                        screen: { type: 'integration', integration: integration.name },
                    }}
                >
                    {integration.title}
                </BreadcrumbItem>
            </Breadcrumbs>
        </PageHeader>
    );
}

/** Tabs for the integration screen */
function IntegrationTabs(props: {
    screen: NavigationScreenIntegrationState;
    integration: api.Integration;
    organization: api.Organization;
    isInstalledOnOrg: boolean;
}): React.ReactElement {
    const { screen, integration, organization, isInstalledOnOrg } = props;

    return (
        <PageTabs>
            <Tabs>
                <Tab
                    label="About"
                    href={{ current: { tab: 'about' } }}
                    active={screen.tab === 'about'}
                />
                {isInstalledOnOrg && organization.permissions.installIntegration ? (
                    <Tab
                        label={
                            integration.target === api.IntegrationTarget.Organization
                                ? 'Installation'
                                : 'Installed on'
                        }
                        href={{ current: { tab: 'installations' } }}
                        active={screen.tab === 'installations'}
                    />
                ) : null}
            </Tabs>
        </PageTabs>
    );
}

/**
 * Panel listing the integration site installations
 */
function IntegrationSiteInstallations(
    props: IntegrationInstallationContext & {
        installation: api.IntegrationInstallation;
    }
): React.ReactElement {
    const { organization, integration, installation } = props;

    const { data: orgAllContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: organization.id,
        })
    );

    const {
        data: { items: siteInstallations },
        next,
        previous,
    } = useSuspensePaginatedAPIQuery(
        apiResource('listIntegrationInstallationSites', {
            integrationName: integration.name,
            installationId: installation.id,
            extended: true,
        })
    );

    const renderSiteInstallationItem = React.useCallback(
        ({ item }: ListRenderItemInfo<api.IntegrationSiteInstallation>) => {
            assertIsExtendedSiteInstallation(item);
            const siteData = getSiteFromOrgAllContent(orgAllContent, item.site.id);
            assertIsDefined(siteData?.item, 'Site structure is required for site installation');
            return (
                <SiteInstallationItem
                    {...props}
                    siteInstallation={item}
                    siteStructure={siteData.item.structure}
                />
            );
        },
        [props]
    );

    return (
        <Panel>
            <Heading>Docs sites</Heading>
            <Description>
                {`${integration.title} is configured and ready to be used in these docs sites.`}
            </Description>

            <PanelBody>
                {siteInstallations.length !== 0 ? (
                    <>
                        <List items={siteInstallations} renderItem={renderSiteInstallationItem} />

                        <Pagination next={next} previous={previous} />
                    </>
                ) : (
                    <IntegrationInstallationsEmptyState integration={integration} />
                )}
            </PanelBody>
        </Panel>
    );
}

/**
 * Panel listing the integration space installations
 */
function IntegrationSpaceInstallations(
    props: IntegrationInstallationContext & {
        installation: api.IntegrationInstallation;
    }
): React.ReactElement {
    const { integration, installation } = props;

    const {
        data: { items: spaceInstallations },
        next,
        previous,
    } = useSuspensePaginatedAPIQuery(
        apiResource('listIntegrationInstallationSpaces', {
            integrationName: integration.name,
            installationId: installation.id,
            extended: true,
        })
    );

    const renderSpaceInstallationItem = React.useCallback(
        ({ item }: ListRenderItemInfo<api.IntegrationSpaceInstallation>) => {
            assertIsExtendedSpaceInstallation(item);
            return <SpaceInstallationItem {...props} spaceInstallation={item} />;
        },
        [props]
    );

    return (
        <Panel>
            <Heading>Spaces</Heading>
            <Description>
                {`${integration.title} is configured and ready to be used in these spaces.`}
            </Description>

            <PanelBody>
                {spaceInstallations.length > 0 ? (
                    <>
                        <List items={spaceInstallations} renderItem={renderSpaceInstallationItem} />

                        <Pagination next={next} previous={previous} />
                    </>
                ) : (
                    <IntegrationInstallationsEmptyState integration={integration} />
                )}
            </PanelBody>
        </Panel>
    );
}

function DangerZonePanel(
    props: MandateProps<IntegrationInstallationContext, 'installation'>
): React.ReactElement {
    const { integration, installation } = props;
    const { navigate } = useNavigation();

    const isAll = integration.target === api.IntegrationTarget.All;
    const isSite = integration.target === api.IntegrationTarget.Site;

    return (
        <Panel kind="danger">
            <Heading id="danger">Danger zone</Heading>
            <PanelBody>
                <Group className="flex items-center justify-between">
                    <Group>
                        <Label>Uninstall from organization</Label>
                        <Description>
                            {`The integration will be removed along with any configurations on your ${isAll ? 'docs sites and spaces' : isSite ? 'docs sites' : 'spaces'}.`}
                        </Description>
                    </Group>
                    <Button
                        label="Uninstall"
                        kind="danger"
                        leadingIcon={Icon.IntegrationsUninstall}
                        onPress={() => {
                            void confirm({
                                title: 'Uninstall integration from organization?',
                                description:
                                    'Uninstalling this integration from your organization will also remove it where it is currently installed. All configuration settings for will be lost.',
                                kind: 'danger',
                                confirmLabel: 'Yes, uninstall',
                                onConfirm: async (_event, { preventClose }) => {
                                    await toast.catch(
                                        async () => {
                                            await uninstallIntegrationFromOrganization({
                                                integration,
                                                installation,
                                            });

                                            navigate({ current: { tab: 'about' } });

                                            toast.success(
                                                'Integration uninstalled from organization'
                                            );
                                        },
                                        'Error while uninstalling integration from organization',
                                        { onReject: preventClose }
                                    );
                                },
                            });
                        }}
                    />
                </Group>
            </PanelBody>
        </Panel>
    );
}

function SiteInstallationItem(
    props: IntegrationInstallationContext & {
        siteInstallation: api.IntegrationSiteInstallation;
        siteStructure: api.SiteStructure;
    }
): React.ReactElement {
    const { siteInstallation, siteStructure } = props;

    assertIsExtendedSiteInstallation(siteInstallation);
    const { site } = siteInstallation;

    return (
        <InstallationItem
            {...props}
            context={{
                type: 'site',
                installation: siteInstallation,
                siteStructure,
            }}
        >
            <ListItemIndicator>
                <Icon.DocsSite className="size-5" />
            </ListItemIndicator>
            <ListItemBody>
                <NavigationLink to={{ screen: { type: 'site', site: site.id } }}>
                    {site.title}
                </NavigationLink>
            </ListItemBody>
        </InstallationItem>
    );
}

function SpaceInstallationItem(
    props: IntegrationInstallationContext & {
        spaceInstallation: api.IntegrationSpaceInstallation;
    }
): React.ReactElement {
    const { spaceInstallation } = props;

    assertIsExtendedSpaceInstallation(spaceInstallation);
    const { space } = spaceInstallation;

    return (
        <InstallationItem {...props} context={{ type: 'space', installation: spaceInstallation }}>
            <ListItemIndicator>
                <SpaceIcon space={space} size={20} />
            </ListItemIndicator>
            <ListItemBody>
                <NavigationLink to={{ screen: { type: 'space', space: space.id, page: '' } }}>
                    {space.title}
                </NavigationLink>
            </ListItemBody>
        </InstallationItem>
    );
}

function InstallationItem(
    props: IntegrationInstallationContext & {
        context: ContentInstallationContext;
        children: React.ReactNode;
    }
): React.ReactElement {
    const { integration, context, organization, children } = props;
    const isInstallationPending = context.installation.status === 'pending';

    const { id: contentId, title: contentTitle } = getContentDataFromInstallationContext(context);

    const hasControlPermissions = (() => {
        if (context.type === 'space') {
            return context.installation.space.permissions.installIntegration;
        }

        if (context.type === 'site') {
            assertIsDefined(context.siteStructure, 'Site structure is required for site context');
            const sitePermissions = getSitePermissions({
                siteStructure: context.siteStructure,
                organization,
            });

            return sitePermissions.installIntegration;
        }

        assertNever(context, `Unknown content type: ${context}`);
    })();

    return (
        <ListItem className="rounded-none border-b last-of-type:border-none">
            {children}

            {hasControlPermissions ? (
                <ListItemControls when="always" className="flex items-center gap-4">
                    {integrationNeedsConfiguration(integration) ? (
                        <Button
                            label={
                                isInstallationPending
                                    ? 'Complete configuration'
                                    : 'Edit configuration'
                            }
                            leadingIcon={
                                isInstallationPending ? (
                                    <Icon.Warning className="text-warning" />
                                ) : (
                                    Icon.Settings
                                )
                            }
                            kind="secondary"
                            href={{
                                current: {
                                    installation: {
                                        step: 'configure',
                                        context: { type: context.type, id: contentId },
                                    },
                                },
                            }}
                        />
                    ) : null}
                    <Button
                        label="Uninstall"
                        leadingIcon={Icon.X}
                        kind="secondary"
                        onPress={async () => {
                            void confirm({
                                title: `Uninstall integration from "${contentTitle}"?`,
                                description: `All configuration settings for this integration in this ${context.type} will be lost.`,
                                kind: 'danger',
                                confirmLabel: 'Yes, uninstall',
                                onConfirm: async (_event, { preventClose }) => {
                                    await toast.catch(
                                        async () => {
                                            if (context.type === 'site') {
                                                return uninstallIntegrationFromSite({
                                                    integrationName: integration.name,
                                                    installationId:
                                                        context.installation.installation,
                                                    siteId: contentId,
                                                    organizationId: organization.id,
                                                });
                                            }

                                            return uninstallIntegrationFromSpace({
                                                integrationName: integration.name,
                                                installationId: context.installation.installation,
                                                spaceId: contentId,
                                                organizationId: organization.id,
                                            });
                                        },
                                        `Error while uninstalling integration from ${context.type}`,
                                        { onReject: preventClose }
                                    );
                                },
                            });
                        }}
                    />
                </ListItemControls>
            ) : null}
        </ListItem>
    );
}

function IntegrationInstallationsEmptyState(props: {
    integration: api.Integration;
}): React.ReactElement {
    const { integration } = props;
    const { navigate } = useNavigation();

    const isTargetAll = integration.target === api.IntegrationTarget.All;
    const isTargetSiteOnly = integration.target === api.IntegrationTarget.Site;

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Integrations />
            </EmptyStateIcon>
            <Heading>No installations found.</Heading>
            <Description>
                You haven't installed that integration on any{' '}
                {isTargetAll ? 'site or space' : isTargetSiteOnly ? 'site' : 'space'} yet.
            </Description>
            <EmptyStateActions>
                <Button
                    kind="secondary"
                    label={
                        isTargetAll
                            ? 'Install on a space or site'
                            : isTargetSiteOnly
                              ? 'Install on a site'
                              : 'Install on a space'
                    }
                    leadingIcon={Icon.IntegrationsInstall}
                    onPress={() => {
                        navigate({
                            current: { installation: { step: 'flow' } },
                        });
                    }}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Dialog for the installation flow
 */
function IntegrationInstallationDialog(props: {
    integration: api.Integration;
    screen: ReturnType<typeof useIntegrationScreen>;
}): React.ReactElement {
    const { integration, screen } = props;
    const { navigate } = useNavigation();

    const installationFlowDialogState = useDialogTriggerState({
        isOpen: true,
        onOpenChange: (isOpen) => {
            if (!isOpen) {
                navigate({ current: { installation: undefined } });
            }
        },
    });

    return (
        <DialogRoot {...installationFlowDialogState.rootProps}>
            <Dialog size={{ width: 960, height: 640 }}>
                <Sentry.ErrorBoundary fallback={<IntegrationsEmptyState kind="no-installation" />}>
                    <React.Suspense fallback={<LoadingPanel />}>
                        <IntegrationInstallationDialogContent
                            screen={screen}
                            integration={integration}
                        />
                    </React.Suspense>
                </Sentry.ErrorBoundary>
            </Dialog>
        </DialogRoot>
    );
}
